import {
  AfterViewInit,
  Component,
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  ViewContainerRef,
} from '@angular/core'
import { SearchResult } from '../../gql/generated'

@Directive({
  selector: '[appHoverEffect]',
  standalone: true,
})
export class HoverEffectDirective implements AfterViewInit, OnDestroy {
  @Input() hoverScale = 1
  @Input() transitionDuration = '0.1s'
  @Input() hoverDelay = 200
  @Input() data!: SearchResult
  @Input() minHeight = '400px'
  @Input() componentType
  private componentRef: ComponentRef<Component> | null = null
  private isHovered = false
  private originalHeight!: string
  private originalWidth!: string
  private hoverTimeoutId: number | undefined
  private isLargeScreen = false
  private hoverCardWidthMultiplier = 1.2

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef
  ) {
    // Initial check
    this.checkWindowSize = this.checkWindowSize.bind(this)
    this.checkWindowSize()
    // Listen to window resize events
    this.renderer.listen('window', 'resize', this.checkWindowSize)
  }

  ngAfterViewInit(): void {
    const nativeElement = this.el.nativeElement as HTMLElement
    this.originalHeight = `${nativeElement.offsetHeight}px`
    this.originalWidth = `${nativeElement.offsetWidth}px`
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.isLargeScreen) return

    this.isHovered = true
    this.hoverTimeoutId = window.setTimeout(() => {
      this.calculateHoverWidth()
      this.createHoverCard()
      this.applyHoverEffect()
    }, this.hoverDelay)
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (!this.isLargeScreen) return

    if (this.hoverTimeoutId !== undefined) {
      clearTimeout(this.hoverTimeoutId)
      this.hoverTimeoutId = undefined
    }
    if (!this.isHovered) {
      this.resetHoverEffect()
    }
  }

  private checkWindowSize(): void {
    this.isLargeScreen = window.innerWidth > 992
    if (!this.isLargeScreen) {
      this.resetHoverEffect()
      this.destroyHoverCard()
    }
  }

  private createHoverCard(): void {
    if (!this.componentRef && this.isLargeScreen) {
      this.componentRef = this.viewContainerRef.createComponent(
        this.componentType
      )

      // Assuming the component has a data property
      this.componentRef.instance['data'] = this.data

      const hostElement = this.el.nativeElement as HTMLElement
      const componentElement = this.componentRef.location.nativeElement as HTMLElement

      // Set z-index on host element
      this.renderer.setStyle(hostElement, 'z-index', '100')

      // Remove from its current position in the DOM
      componentElement.parentNode?.removeChild(componentElement);

      // Append as child to the host element
      hostElement.appendChild(componentElement);

      console.log(this.componentRef.instance)
      this.componentRef.instance['fetch']()
      this.componentRef.changeDetectorRef.detectChanges();

      this.setInitialStyles(componentElement)
      this.positionHoverCard(componentElement, hostElement)
      this.showHoverCard(componentElement)

      this.renderer.listen(componentElement, 'mouseenter', () => {
        this.isHovered = true
      })
      this.renderer.listen(componentElement, 'mouseleave', () =>
        this.onPreviewMouseLeave()
      )
    }
  }

  private setInitialStyles(element: HTMLElement): void {
    this.renderer.setStyle(element, 'position', 'absolute')
    this.renderer.setStyle(element, 'top', '0')
    this.renderer.setStyle(element, 'left', '0')
    this.renderer.setStyle(element, 'height', "105%");
    this.renderer.setStyle(element, 'min-height', this.minHeight)
    this.renderer.setStyle(element, 'width', this.calculateHoverWidth())
    this.renderer.setStyle(element, 'z-index', '9000')
    this.renderer.setStyle(
      element,
      'transition',
      `transform ${this.transitionDuration}, opacity ${this.transitionDuration}`
    )
    this.renderer.setStyle(element, 'opacity', '0')
  }

  private calculateHoverWidth(): string {
    const width = Math.max(
      parseFloat(this.originalWidth) * this.hoverCardWidthMultiplier,
      500
    )
    return `${width}px`
  }

  private positionHoverCard(
    previewElem: HTMLElement,
    hostElem: HTMLElement
  ): void {
    const hostRect = hostElem.getBoundingClientRect()
    const previewRect = previewElem.getBoundingClientRect()
    const viewportWidth = window.innerWidth

    let leftAffix = (previewRect.width - hostRect.width) / -2

    // const overflowLeft = hostRect.left + leftAffix - viewportWidth
    const overflowRight =
      hostRect.left + leftAffix + previewRect.width - viewportWidth

    if (hostRect.left + leftAffix < 50) {
      leftAffix = 0
    }

    if (overflowRight > -50) {
      leftAffix -= (previewRect.width - hostRect.width) / 2
    }

    leftAffix = Math.min(leftAffix, viewportWidth - previewRect.width)

    this.renderer.setStyle(previewElem, 'left', `${leftAffix}px`)
    this.renderer.setStyle(previewElem, 'top', `-5%`)
  }

  private showHoverCard(element: HTMLElement): void {
    setTimeout(() => {
      this.renderer.setStyle(element, 'opacity', '1')
      this.renderer.setStyle(element, 'transform', `scale(${this.hoverScale})`)
    }, 0)
  }

  private onPreviewMouseLeave(): void {
    this.isHovered = false
    this.resetHoverEffect()
    this.destroyHoverCard()
  }

  private applyHoverEffect(): void {
    const hostElement = this.el.nativeElement as HTMLElement
    this.renderer.setStyle(
      hostElement,
      'transition',
      `transform ${this.transitionDuration}`
    )
    this.renderer.setStyle(
      hostElement,
      'transform',
      `scale(${this.hoverScale})`
    )
    // this.renderer.setStyle(hostElement, 'height', this.originalHeight)
  }

  private resetHoverEffect(): void {
    const hostElement = this.el.nativeElement as HTMLElement
    this.renderer.setStyle(hostElement, 'transform', 'scale(1)')
    this.renderer.setStyle(hostElement, 'height', '100%')
    this.renderer.setStyle(hostElement, 'width', 'auto')
    this.renderer.setStyle(
      hostElement,
      'transition',
      `transform ${this.transitionDuration}`
    )

    // Reset z-index
    this.renderer.removeStyle(hostElement, 'z-index')
  }

  private destroyHoverCard(): void {
    if (this.componentRef) {
      const componentElement = this.componentRef.location
        .nativeElement as HTMLElement
      this.renderer.setStyle(componentElement, 'opacity', '0')
      this.renderer.setStyle(
        componentElement,
        'transform',
        `scale(${this.hoverScale - 0.1})`
      )

      setTimeout(
        () => {
          this.componentRef?.destroy()
          this.componentRef = null
        },
        parseFloat(this.transitionDuration) * 1000
      )
    }
  }

  ngOnDestroy(): void {
    if (this.hoverTimeoutId !== undefined) {
      clearTimeout(this.hoverTimeoutId)
    }
    this.destroyHoverCard()
  }
}
